/**
 * Wishlist Manager for Torrini Jewels
 * Handles wishlist functionality with localStorage persistence
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'torrini_wishlist';
  const WISHLIST_CHANGED_EVENT = 'wishlist:changed';

  // Product data cache to avoid refetching
  const productCache = {};

  /**
   * WishlistManager - Core wishlist functionality
   */
  const WishlistManager = {
    /**
     * Feather's `x` glyph, as a string.
     *
     * The wishlist card is built here as an HTML template literal, so it cannot
     * call `{% render 'icon-feather' %}` the way the Liquid call sites do. It
     * used to emit `<i data-feather="x">` and rely on feather.replace() running
     * afterwards; that library is gone, so the markup is inline.
     *
     * Kept byte-identical to what snippets/icon-feather.liquid emits for 'x'.
     * If that snippet's wrapper attributes change, change this too - it is the
     * one copy Liquid cannot keep in sync.
     */
    ICON_X: '<svg class="feather feather-x" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',

    /**
     * Get all wishlist items from localStorage
     * @returns {Array} Array of wishlist items with productId, handle, and addedAt
     */
    getItems: function() {
      try {
        const items = localStorage.getItem(STORAGE_KEY);
        return items ? JSON.parse(items) : [];
      } catch (e) {
        console.error('Error reading wishlist from localStorage:', e);
        return [];
      }
    },

    /**
     * Save wishlist items to localStorage
     * @param {Array} items - Array of wishlist items
     */
    saveItems: function(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        this.emit();
      } catch (e) {
        console.error('Error saving wishlist to localStorage:', e);
      }
    },

    /**
     * Get the count of items in the wishlist
     * @returns {number} Number of items
     */
    getCount: function() {
      return this.getItems().length;
    },

    /**
     * Check if a product is in the wishlist
     * @param {string|number} productId - The product ID to check
     * @returns {boolean} True if the product is in the wishlist
     */
    isInWishlist: function(productId) {
      const items = this.getItems();
      return items.some(item => String(item.productId) === String(productId));
    },

    /**
     * Add a product to the wishlist
     * @param {string|number} productId - The product ID
     * @param {string} handle - The product handle (URL slug)
     * @returns {boolean} True if added, false if already existed
     */
    add: function(productId, handle) {
      if (this.isInWishlist(productId)) {
        return false;
      }

      const items = this.getItems();
      items.push({
        productId: String(productId),
        handle: handle,
        addedAt: new Date().toISOString()
      });

      this.saveItems(items);
      return true;
    },

    /**
     * Remove a product from the wishlist
     * @param {string|number} productId - The product ID to remove
     * @returns {boolean} True if removed, false if not found
     */
    remove: function(productId) {
      const items = this.getItems();
      const filteredItems = items.filter(item => String(item.productId) !== String(productId));

      if (filteredItems.length === items.length) {
        return false;
      }

      this.saveItems(filteredItems);
      return true;
    },

    /**
     * Toggle a product in the wishlist
     * @param {string|number} productId - The product ID
     * @param {string} handle - The product handle (required for adding)
     * @returns {boolean} True if now in wishlist, false if removed
     */
    toggle: function(productId, handle) {
      if (this.isInWishlist(productId)) {
        this.remove(productId);
        return false;
      } else {
        this.add(productId, handle);
        return true;
      }
    },

    /**
     * Clear all items from the wishlist
     */
    clear: function() {
      this.saveItems([]);
    },

    /**
     * Emit a custom event when wishlist changes
     */
    emit: function() {
      const event = new CustomEvent(WISHLIST_CHANGED_EVENT, {
        detail: {
          count: this.getCount(),
          items: this.getItems()
        }
      });
      document.dispatchEvent(event);
    },

    /**
     * Fetch product data from Shopify AJAX API
     * @param {string} handle - The product handle
     * @returns {Promise} Promise resolving to product data
     */
    fetchProduct: function(handle) {
      // Check cache first
      if (productCache[handle]) {
        return Promise.resolve(productCache[handle]);
      }

      return fetch(`/products/${handle}.js`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch product: ${handle}`);
          }
          return response.json();
        })
        .then(product => {
          // Cache the product data
          productCache[handle] = product;
          return product;
        });
    },

    /**
     * Fetch all products in the wishlist
     * @returns {Promise} Promise resolving to array of product data
     */
    fetchAllProducts: function() {
      const items = this.getItems();
      const promises = items.map(item =>
        this.fetchProduct(item.handle).catch(err => {
          console.error(`Error fetching product ${item.handle}:`, err);
          return null;
        })
      );

      return Promise.all(promises).then(products =>
        products.filter(product => product !== null)
      );
    },

    /**
     * Refresh the wishlist drawer UI
     */
    refreshDrawer: function() {
      const itemsContainer = document.getElementById('wishlistItems');
      const emptyState = document.getElementById('wishlistEmpty');

      if (!itemsContainer) return;

      const items = this.getItems();

      // Show empty state if no items
      if (items.length === 0) {
        itemsContainer.innerHTML = '';
        if (emptyState) {
          emptyState.style.display = '';
          itemsContainer.appendChild(emptyState);
        }
        return;
      }

      // Show loading state
      itemsContainer.innerHTML = '<div class="wishlist__loading">Loading wishlist...</div>';

      // Fetch all products and render
      this.fetchAllProducts()
        .then(products => {
          if (products.length === 0) {
            itemsContainer.innerHTML = '';
            if (emptyState) {
              emptyState.style.display = '';
              itemsContainer.appendChild(emptyState);
            }
            return;
          }

          // Build items HTML
          let html = '';
          products.forEach(product => {
            html += this.renderWishlistItem(product);
          });

          itemsContainer.innerHTML = html;


          // Attach event listeners to buttons
          this.attachDrawerEventListeners();
        })
        .catch(error => {
          console.error('Error loading wishlist items:', error);
          itemsContainer.innerHTML = '<div class="wishlist__error">Error loading wishlist. Please try again.</div>';
        });
    },

    /**
     * Render a single wishlist item HTML
     * @param {Object} product - The product data from Shopify AJAX API
     * @returns {string} HTML string for the item
     */
    renderWishlistItem: function(product) {
      const imageUrl = product.featured_image
        ? product.featured_image.replace(/(\.[^.]+)$/, '_600x600$1')
        : '';

      // Format price
      const price = this.formatMoney(product.price);

      // Build a selector for every product option that has a real choice, matching
      // variants on the full option tuple (mirrors the product page's C3 logic). The
      // /products/{handle}.js `options` payload can be strings or {name} objects.
      const variants = product.variants || [];
      const optionNames = (product.options || []).map(opt =>
        typeof opt === 'string' ? opt : ((opt && opt.name) || '')
      );

      let optionSelectors = '';
      if (variants.length > 1) {
        optionNames.forEach((name, idx) => {
          const values = [...new Set(
            variants.map(v => (v.options || [])[idx]).filter(val => val != null && val !== '')
          )];
          // Nothing to choose if the option collapses to a single value — skip it
          // (the tuple match below treats that index as a wildcard).
          if (values.length <= 1) return;

          optionSelectors += `
            <div class="wishlist-product-card__size-selector">
              <label class="wishlist-product-card__size-label">${this.escapeHtml(name)}</label>
              <select class="wishlist-product-card__size-select" data-product-id="${product.id}" data-option-index="${idx}">
                <option value="">Select ${this.escapeHtml(name)}</option>
                ${values.map(val => {
                  const safe = this.escapeHtml(String(val));
                  return `<option value="${safe}">${safe}</option>`;
                }).join('')}
              </select>
            </div>
          `;
        });
      }

      // Get first available variant ID for simple / single-choice products
      const firstVariantId = variants.length > 0 ? variants[0].id : '';

      // When there are real selectors to fill, start disabled and enable once every
      // option is chosen. When there are none, add the first/only variant directly —
      // never render a permanently un-addable button.
      const hasSelectors = optionSelectors !== '';
      const variantsAttr = JSON.stringify(variants).replace(/'/g, '&#39;');
      const addToCartButton = hasSelectors
        ? `<button class="wishlist-product-card__add-to-cart" data-product-id="${product.id}" data-variants='${variantsAttr}' disabled>Add to cart</button>`
        : `<button class="wishlist-product-card__add-to-cart" data-product-id="${product.id}" data-variant-id="${firstVariantId}">Add to cart</button>`;

      return `
        <div class="wishlist-product-card" data-product-id="${product.id}" data-product-handle="${product.handle}">
          <a href="/products/${product.handle}" class="wishlist-product-card__image-link">
            <div class="wishlist-product-card__image">
              ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" class="wishlist-product-card__img" loading="lazy">` : ''}
            </div>
          </a>
          <div class="wishlist-product-card__info">
            <div class="wishlist-product-card__header">
              <a href="/products/${product.handle}" class="wishlist-product-card__title">${this.escapeHtml(product.title)}</a>
              <button class="wishlist-product-card__remove" data-product-id="${product.id}" aria-label="Remove from wishlist">
                ${WishlistManager.ICON_X}
              </button>
            </div>
            <p class="wishlist-product-card__price">${price}</p>
            ${optionSelectors}
            ${addToCartButton}
          </div>
        </div>
      `;
    },

    /**
     * Format money value using Shopify's money format
     * @param {number} cents - Price in cents
     * @returns {string} Formatted price
     */
    formatMoney: function(cents) {
      // Use the shop's money format or default to USD-style
      const amount = (cents / 100).toFixed(2);
      // Check if Shopify's money format function is available
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents);
      }
      // Default format with currency symbol (assume EUR for Torrini)
      return '€' + amount.replace('.', ',');
    },

    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} Escaped HTML
     */
    escapeHtml: function(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /**
     * Attach event listeners to drawer buttons
     */
    attachDrawerEventListeners: function() {
      // Remove buttons
      const removeButtons = document.querySelectorAll('.wishlist-product-card__remove');
      removeButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const productId = btn.dataset.productId;
          this.remove(productId);
          this.refreshDrawer();
          this.updateAllHeartButtons();
        });
      });

      // Option selectors - enable add to cart only when EVERY option for the product
      // has been chosen (supports multi-option products, not just a single size).
      const sizeSelectors = document.querySelectorAll('.wishlist-product-card__size-select');
      sizeSelectors.forEach(select => {
        select.addEventListener('change', (e) => {
          const productId = select.dataset.productId;
          const addToCartBtn = document.querySelector(`.wishlist-product-card__add-to-cart[data-product-id="${productId}"]`);
          if (!addToCartBtn) return;

          const productSelects = document.querySelectorAll(`.wishlist-product-card__size-select[data-product-id="${productId}"]`);
          const allChosen = Array.from(productSelects).every(s => s.value);
          addToCartBtn.disabled = !allChosen;
        });
      });

      // Add to cart buttons
      const addToCartButtons = document.querySelectorAll('.wishlist-product-card__add-to-cart');
      addToCartButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleAddToCart(btn);
        });
      });
    },

    /**
     * Handle add to cart from wishlist
     * @param {HTMLElement} btn - The add to cart button
     */
    handleAddToCart: function(btn) {
      const productId = btn.dataset.productId;
      let variantId = btn.dataset.variantId;

      // Wishlist cards carry no error slot, so feedback goes on the button itself.
      // Same shape as the failure path below: swap the label, restore after 2s.
      const buttonLabel = btn.textContent;
      function flashButton(message) {
        btn.textContent = message;
        btn.disabled = true;
        setTimeout(() => {
          btn.textContent = buttonLabel;
          btn.disabled = false;
        }, 2000);
      }

      // If no direct variant ID, resolve it from the option selections. Build a sparse
      // array keyed by each select's TRUE option index, then match on the full tuple
      // (skipping unselected/wildcard holes) — never a single positional compare.
      if (!variantId) {
        const variants = JSON.parse(btn.dataset.variants || '[]');
        const selects = document.querySelectorAll(`.wishlist-product-card__size-select[data-product-id="${productId}"]`);

        const selectedOptions = [];
        let allChosen = true;
        selects.forEach(sel => {
          const idx = parseInt(sel.dataset.optionIndex, 10);
          if (Number.isNaN(idx)) return;
          if (sel.value) {
            selectedOptions[idx] = sel.value;
          } else {
            allChosen = false;
          }
        });

        if (!allChosen) {
          flashButton('Select all options');
          return;
        }

        const matchedVariant = variants.find(v =>
          (v.options || []).every((opt, i) => !selectedOptions[i] || opt === selectedOptions[i])
        );
        if (matchedVariant) {
          variantId = matchedVariant.id;
        }
      }

      if (!variantId) {
        flashButton('Select a size');
        return;
      }

      // Disable button and show loading state
      const originalText = buttonLabel;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      // Add to cart via AJAX
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
      // Read the body before branching: a 422 carries Shopify's shopper-facing
      // `description` (sold out, over stock, cart limit). Throwing on !ok before
      // parsing discarded that reason and left the user with a bare 'Error'.
      .then(response => response.json().then(data => {
        if (!response.ok) {
          const err = new Error((data && (data.description || data.message)) || 'Failed to add to cart');
          err.shopperMessage = data && data.description;
          throw err;
        }
        return data;
      }))
      .then(data => {
        // Success - refresh cart if function exists and open mini cart.
        // These moved from bare globals onto the Torrini namespace (L7); the
        // typeof guards are kept so a missing drawer degrades quietly.
        const T = window.Torrini || {};

        function closeWishlistThenOpenCart() {
          if (typeof T.openMiniCart !== 'function') return;
          if (typeof T.closeWishlist === 'function') {
            T.closeWishlist();
          }
          setTimeout(() => {
            T.openMiniCart();
          }, 300);
        }

        if (typeof T.refreshCartSection === 'function') {
          T.refreshCartSection().then(closeWishlistThenOpenCart);
        } else {
          closeWishlistThenOpenCart();
        }

        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        // A 422 means Shopify refused the line (sold out / over stock / limit) as
        // opposed to the request failing outright, so say so. The full sentence is
        // too long for a button, so it goes on the title attribute.
        if (error && error.shopperMessage) {
          btn.textContent = 'Unavailable';
          btn.title = error.shopperMessage;
        } else {
          btn.textContent = 'Error';
        }
        setTimeout(() => {
          btn.textContent = originalText;
          btn.removeAttribute('title');
          btn.disabled = false;
        }, 2000);
      });
    },

    /**
     * Update all heart buttons on the page to reflect wishlist state
     */
    updateAllHeartButtons: function() {
      const heartButtons = document.querySelectorAll('[data-product-id].product-card__wishlist');

      heartButtons.forEach(btn => {
        const productId = btn.dataset.productId;
        if (this.isInWishlist(productId)) {
          btn.classList.add('is-active');
        } else {
          btn.classList.remove('is-active');
        }
      });
    },

    /**
     * Update the header wishlist badge count
     */
    updateHeaderBadge: function() {
      const badges = document.querySelectorAll('.header__wishlist-count');
      const count = this.getCount();

      badges.forEach(badge => {
        if (count > 0) {
          badge.textContent = count;
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      });
    },

    /**
     * Initialize wishlist functionality
     */
    init: function() {
      // Update all heart buttons on page load
      this.updateAllHeartButtons();

      // Update header badge
      this.updateHeaderBadge();

      // Listen for wishlist changes
      document.addEventListener(WISHLIST_CHANGED_EVENT, () => {
        this.updateHeaderBadge();
        this.updateAllHeartButtons();
      });

      // Set up click handlers for wishlist buttons
      this.setupWishlistButtons();
    },

    /**
     * Set up click handlers for wishlist buttons
     */
    setupWishlistButtons: function() {
      document.addEventListener('click', (e) => {
        const wishlistBtn = e.target.closest('.product-card__wishlist[data-product-id]');

        if (wishlistBtn) {
          e.preventDefault();
          e.stopPropagation();

          const productId = wishlistBtn.dataset.productId;
          const productHandle = wishlistBtn.dataset.productHandle;

          const isNowInWishlist = this.toggle(productId, productHandle);

          // Update button state
          if (isNowInWishlist) {
            wishlistBtn.classList.add('is-active');
          } else {
            wishlistBtn.classList.remove('is-active');
          }

          // The only feedback is a heart filling in, which a screen reader
          // does not see: the button's own label does not change and nothing
          // takes focus. aria-pressed would cover the state, but not the fact
          // that the state just changed in response to this click.
          if (window.Torrini && typeof window.Torrini.announce === 'function') {
            window.Torrini.announce(
              isNowInWishlist ? 'Added to your wishlist' : 'Removed from your wishlist'
            );
          }
        }
      });
    }
  };

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', function() {
    WishlistManager.init();
  });

  // Re-sync after a theme-editor section reload. The click handling is
  // delegated from document so it survives on its own, but freshly rendered
  // heart buttons and the header badge start blank and need their state
  // reapplied. init() is deliberately NOT re-run: it would register a second
  // delegated click listener and a second wishlist-changed listener.
  document.addEventListener('shopify:section:load', function() {
    WishlistManager.updateAllHeartButtons();
    WishlistManager.updateHeaderBadge();
  });

  // Handle bfcache restoration (browser back/forward navigation)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      // Page was restored from bfcache - update UI
      WishlistManager.updateAllHeartButtons();
      WishlistManager.updateHeaderBadge();
    }
  });

  // Expose globally for use in other scripts
  window.WishlistManager = WishlistManager;

})();
